import React, { useMemo, useState } from 'react';
import { Paper, Button, Box, CircularProgress, Alert } from '@mui/material';
import { InsightsRounded } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { FilterBar, Field, SearchInput, SelectInput, Spacer, ResetButton } from '../../components/common';
import DataTable, { Column } from '../../components/DataTable';
import NameCell from '../../components/NameCell';
import StatusChip from '../../components/StatusChip';
import { useBusinesses, BusinessListItem } from '../../hooks/useBusinesses';
import { money } from '../../utils/format';
import { statusLabel } from '../../utils/format';

const BusinessesPage: React.FC = () => {
  const { items, filters, loading, error, reload } = useBusinesses();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [city, setCity] = useState('');
  const [status, setStatus] = useState('');

  const rows = useMemo(
    () =>
      items.filter(
        (r) =>
          (!q || (r.name + r.owner + r.city + r.id).toLowerCase().includes(q.toLowerCase())) &&
          (!cat || r.category === cat) && (!city || r.city === city) && (!status || r.status === status)
      ),
    [items, q, cat, city, status]
  );

  const columns: Column<BusinessListItem>[] = [
    { key: 'name', label: 'Business', csv: (r) => r.name, render: (r) => <NameCell name={r.name} /> },
    { key: 'owner', label: 'Owner' },
    { key: 'category', label: 'Category' },
    { key: 'city', label: 'City' },
    { key: 'queues', label: 'Queues', align: 'right' },
    { key: 'revenue', label: 'Revenue', align: 'right', csv: (r) => r.revenue, render: (r) => money(r.revenue) },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
    {
      key: '_', label: '', render: (r) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button component={Link} to={`/businesses/${r.id}/dashboard`} size="small" variant="outlined" startIcon={<InsightsRounded />}
            sx={{ borderColor: 'divider', color: 'primary.main' }}>
            Dashboard
          </Button>
        </Box>
      ),
    },
  ];

  const reset = () => { setQ(''); setCat(''); setCity(''); setStatus(''); };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 300, alignItems: 'center' }}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Retry</Button>}>{error}</Alert>;
  }

  return (
    <>
      <FilterBar>
        <Field label="Search"><SearchInput value={q} onChange={setQ} placeholder="Business, owner…" /></Field>
        <Field label="Category"><SelectInput value={cat} onChange={setCat} options={[{ value: '', label: 'All categories' }, ...filters.categories]} /></Field>
        <Field label="City"><SelectInput value={city} onChange={setCity} options={[{ value: '', label: 'All cities' }, ...filters.cities]} /></Field>
        <Field label="Status"><SelectInput value={status} onChange={setStatus} options={[{ value: '', label: 'All statuses' }, ...filters.statuses.map((s) => ({ value: s, label: statusLabel(s) }))]} /></Field>
        <Spacer />
        <ResetButton onClick={reset} />
      </FilterBar>

      <Paper sx={{ p: 0 }}>
        <DataTable columns={columns} rows={rows} title="Businesses" exportName="businesses" />
      </Paper>
    </>
  );
};

export default BusinessesPage;
